/**
 * The Report Studio composer — where the TD writes the report and moves it down the
 * pipeline.
 *
 * Authority: `05-reporting-marking.md` §A5 (the responsibility matrix), §B5a (TD Closing
 * Remarks), §B6 (the report chain, the character contract and the push guards) and §B9 (the
 * states), with ruling §E for the serialisation.
 *
 * Four rules this component exists to keep:
 *
 *  1. **The authoring surface follows ownership.** On an **umpire** the owning UM drafts and
 *     owns the narrative (W3), so the TD gets one box — the 3–4 line closing remarks — and
 *     the UM's text is shown beneath it, read-only, as context. On every other subject the
 *     TD authors the whole report and there are no closing remarks at all.
 *
 *  2. **The preview is the real serialisation.** It is built by `assembleReportText` from
 *     `@hockeytd/shared` — the same function the API assembles with — so the text on screen
 *     is the text that would be pushed. The prefixes are never typed and never doubled.
 *     **The server remains the authority**: its reply to a save replaces the whole report.
 *
 *  3. **Only legal transitions are offered, and only to whoever may make them.** The buttons
 *     are generated from `legalReportStageTransitions`, the same map the route validates
 *     against, so the screen cannot offer a press the API would refuse. `TMS Published` is
 *     in neither: it is confirmed inside TMS and read back from it (§B9).
 *
 *     **The push is the TD's on every report (RUN 96).** Ruling R2' had the owning UM push an
 *     umpire's report (W3) and this screen draw no button for it. The TD's instruction of 27
 *     September 2026 names umpires among the reports HE pushes, through the TMS Push extension
 *     in his own browser, so the push is offered on an umpire too - the assembled report (his
 *     remarks above the UM's narrative) with the agreed marks. The narrative stays hers to write,
 *     and the hint beside the button says so.
 *
 *     **And narrowed again by the text (ruling D-4, §G).** `AI Drafted` and `TD Drafted` are
 *     claims that a draft exists — §A3's progress counters read them — so the API refuses
 *     both on a report holding no text, and the two buttons are disabled here until the
 *     **saved** report holds some. Saved, not typed: the route weighs the stored report, so
 *     text still sitting in the box gets a line saying to save it first rather than a button
 *     that leads to a 422 contradicting the words on screen. The stage row stays where it is;
 *     the push keeps its own §B6 guard and its own copy, and is untouched by this.
 *
 *  6. **The stage pill is server-confirmed, always.** No transition sets it before the reply
 *     lands. A press in flight is shown by disabling the control; a refusal leaves the pill
 *     untouched, because a pill that had already moved would be claiming a stage the server
 *     never granted.
 *
 *  4. **A released report is read-only, and says why.** Stage 4 means TMS holds the
 *     published record; editing our copy afterwards would leave the Control Centre asserting
 *     something TMS does not hold.
 *
 *  5. **A push is a moment, not a state.** The stage model records only the furthest stage
 *     reached, so text edited after a push leaves the report on `TMS Draft` — truthfully,
 *     because TMS really does hold that push. What would *not* be truthful is letting
 *     `TMS Draft · Last Pushed 09:12` stand beside text TMS has never seen, so an
 *     `Edited Since Last Push` chip is drawn beside the pill until the text is pushed again.
 */

import { useEffect, useState } from "react";

import {
  assembleReportText,
  buildReportPayload,
  isReportReleased,
  legalReportStageTransitions,
  REPORT_STAGE_LABELS,
  REPORT_STAGE_TOOLTIPS,
  REPORT_TEXT_REQUIRED_HINT,
  reportStageActionLabel,
  stageRequiresReportText,
  type ReportStage,
  type TmsReportPayload,
} from "@hockeytd/shared";

import { ApiError, postReportStage, putReportText } from "../../api/client.js";
import type { PushReceipt, ReportBody, ReportPushBody } from "../../api/types.js";
import { PushConfirm } from "./PushConfirm.js";
import { PushFallbacks } from "./PushFallbacks.js";
import { PushReceiptNote, pushTime } from "./PushReceiptNote.js";
import {
  EXTENSION_ABSENT_TEXT,
  ReportPushNotLanded,
  pushReportThroughExtension,
  reportPushExtensionPresent,
} from "./reportPushFlow.js";

/**
 * The character contract of §B6. Configurable in Configuration §07; seeded at 800/770.
 *
 * Exported because the UM app's Report Studio counts the SAME field — the 800 is the TMS text
 * box's own limit, and the meter there measures the same assembled text this one does. Two
 * copies of the number would be two answers to "how much is left".
 */
export const LIMIT = 800;
export const NEAR = 770;

/** §B5a's placeholder, verbatim. */
const REMARKS_PLACEHOLDER =
  "Three or four lines — thanks, the team-building spirit, congratulations on reaching the " +
  "final, encouragement.";

/**
 * Ruling D-4's other half. `REPORT_TEXT_REQUIRED_HINT` is for a report with no text anywhere;
 * this is for text that exists but only in the box, where "add text" would be plainly wrong
 * to a TD looking straight at the words they just typed.
 */
const SAVE_FIRST_HINT = "Save your text first — the marker reads the saved report.";

/**
 * §B6's counter measures the assembled text — the preview below, which on an umpire includes
 * the owning UM's narrative. That is right (it is the push guard's own measure) and surprising
 * while typing three lines and watching the number sit in the hundreds, so the caption names
 * what is being counted and against what allowance.
 *
 * `LIMIT` is interpolated rather than written out, so the sentence and the constant cannot
 * drift apart if Configuration §07 moves the number.
 */
export const COUNT_CAPTION = `Counts the whole preview; TMS allows ${String(LIMIT)} characters.`;

/**
 * RUN 96 - what the confirmation says about the press beyond the text: TMS saves the marks with the
 * report, and the TMS Push extension's own window shows both, field by field, before anything is
 * saved there. Confirming here only asks it to.
 */
export const EXTENSION_PUSH_NOTE =
  "The marks go with it - every criterion as saved on the Marking Panel, because TMS saves them " +
  "with the report (every one must be set; none may be N/A). Confirming here opens the TMS Push " +
  "window, which reads the official's marking from TMS and shows every mark and the text again; " +
  "nothing is saved until you Confirm there, and it is saved with Save Draft only.";

/** The read-only box's heading. One sentence, one meaning, in both Report Studios. */
export const PREVIEW_LABEL = "Preview — the full report exactly as TMS will receive it (read-only)";

/** §B6's `TD Draft Report` placeholder, less the AI-draft half, which is a later slice. */
const DRAFT_PLACEHOLDER =
  "Write the report in your own words. This is the text pushed to the TMS marking section " +
  "for this official.";

export type ReportTextWriter = (
  tmsPersonId: string,
  body: { tdClosingRemarks: string | null } | { tdDraftText: string | null },
) => Promise<ReportBody>;

export type ReportStageWriter = (tmsPersonId: string, stage: ReportStage) => Promise<ReportBody>;

/**
 * The push (§B6). It is a **different call** from a stage move: reaching `TMS Draft` means
 * TMS accepted the text, and the API refuses `{ stage: 3 }` on the stage route for exactly
 * that reason.
 *
 * **RUN 96: the push goes through the TMS Push extension** (`reportPushFlow.ts`) - the TD's
 * instruction of 27 September 2026. The press prepares a plan on the server, the extension shows
 * it in its own window and saves it in TMS with Save Draft on the TD's Confirm there, reads it
 * back, and the API records the outcome. `onProgress` carries the extension's stages to the
 * screen while the press is out. The seam exists for the tests.
 */
export type ReportPusher = (tmsPersonId: string, onProgress?: (words: string) => void) => Promise<ReportPushBody>;

export interface ReportComposerProps {
  /** The official's display name, for the panel head and the push copy. */
  officialName: string;
  /**
   * The report as fetched. Held as seed state, which is safe because the Report Studio
   * mounts this component under `key={officialId}` — a different official is a different
   * component instance, never a mutated one.
   */
  initialReport: ReportBody;
  /** Injected in tests only; the real writes are `putReportText` and `postReportStage`. */
  saveText?: ReportTextWriter;
  transition?: ReportStageWriter;
  /** Injected in tests only; the real press goes through the TMS Push extension. */
  push?: ReportPusher;
  /** Injected in tests only: whether the TMS Push extension answers in this browser. */
  extensionPresent?: () => Promise<boolean>;
}

export function counterClass(count: number): string {
  if (count > LIMIT) return "charcount over";
  if (count >= NEAR) return "charcount near";
  return "charcount";
}

export function counterTitle(count: number): string {
  if (count > LIMIT) return `Over The TMS Limit — Trim ${count - LIMIT} Characters`;
  if (count >= NEAR) return `Approaching The TMS Limit — ${LIMIT - count} Characters Left`;
  return "Within The TMS Limit";
}

/**
 * The text as it would go into the TMS field — ruling §E for an umpire, verbatim otherwise.
 * One implementation of the serialisation, shared with the API.
 */
export function previewText(report: ReportBody, draft: string): string {
  return report.ownedByUm
    ? assembleReportText({ tdClosingRemarks: draft, umBody: report.tdDraftText })
    : draft;
}

/**
 * **What a push would actually send.**
 *
 * `POST /officials/:tmsPersonId/report/push` carries no body: it assembles the report the
 * server has STORED and sends that. So the payload is built from `assembledText` — the
 * server's own serialisation of the stored report — and never from the textarea. Everything
 * that claims to describe the push is handed this one object: the confirmation dialogue, the
 * `Copy For TMS` clipboard text, the `Download Word` document and the receipt.
 *
 * It reads only state the client already holds, so §4's fallbacks keep the property that
 * makes them worth having: they need no API, no transport and no network.
 */
export function reportPushPayload(report: ReportBody, officialName: string): TmsReportPayload {
  return buildReportPayload({
    subjectTmsPersonId: report.tmsPersonId,
    subjectName: officialName,
    tournamentId: report.tournamentId,
    text: report.assembledText,
  });
}

/**
 * The transitions this screen may offer on this report.
 *
 * `legalReportStageTransitions` answers "what may happen to a report on this stage"; this
 * narrows it to "what may **the Technical Delegate** do to *this* report". The one narrowing
 * is the push: on a UM-owned report the push is write path W3, taken by the owning Umpires
 * Manager from the UM app, and the API refuses it from the TD. Everything else — the two
 * marker transitions that record a draft and send nothing anywhere — is unchanged.
 */
export function offeredTransitions(report: ReportBody): readonly ReportStage[] {
  // RUN 96: the push is the TD's on every report, umpires included (his instruction of 27
  // September 2026) - the owning UM still writes the narrative and moves the markers.
  return legalReportStageTransitions(report.stage);
}

export function ReportComposer({
  officialName,
  initialReport,
  saveText = putReportText,
  transition = postReportStage,
  push = pushReportThroughExtension,
  extensionPresent = reportPushExtensionPresent,
}: ReportComposerProps): React.JSX.Element {
  const [report, setReport] = useState<ReportBody>(initialReport);
  /** What the TD is typing — the closing remarks on an umpire, the whole report otherwise. */
  const [draft, setDraft] = useState<string>(
    (initialReport.ownedByUm ? initialReport.tdClosingRemarks : initialReport.tdDraftText) ?? "",
  );
  const [busy, setBusy] = useState<boolean>(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * Whether the saved text has moved on since the last push (rule 5).
   *
   * **This is derived session-locally, and deliberately so.** `ReportBody` carries
   * `lastPushedAt` but no corresponding "text last saved at", so there is no pair of server
   * timestamps to compare: the honest derivation from server data alone does not exist yet.
   * What is derived here is therefore the narrower, still-true claim — *this session saved
   * text after this report's last push* — set by a successful save on an already-pushed
   * report and cleared by a successful push. A report loaded fresh shows no chip even if
   * somebody edited it on another device, which is an under-claim rather than an invention.
   * When the API grows a `textUpdatedAt`, this becomes `textUpdatedAt > lastPushedAt` and
   * the flag goes.
   */
  const [editedSincePush, setEditedSincePush] = useState<boolean>(false);
  /** Whether the confirmation dialogue is open. A push is never one click (gate 4). */
  const [confirming, setConfirming] = useState<boolean>(false);
  /** The last attempt, of either outcome — seeded from the server so it survives a reload. */
  const [receipt, setReceipt] = useState<PushReceipt | null>(initialReport.lastReceipt);
  /** Where a push through the TMS Push extension stands, in words, while it is out (RUN 96). */
  const [progress, setProgress] = useState<string | null>(null);
  /**
   * Whether the TMS Push extension answers in this browser - asked as the composer opens and
   * whenever the window regains the focus, as the appointments page asks. `null` while unknown.
   * The push stays pressable either way (the press asks again, for three seconds); an extension
   * that did not answer gets a note saying what to check.
   */
  const [extensionReady, setExtensionReady] = useState<boolean | null>(null);

  useEffect(() => {
    let live = true;
    const look = (): void => {
      void extensionPresent().then(
        (present) => {
          if (live) setExtensionReady(present);
        },
        () => {
          if (live) setExtensionReady(false);
        },
      );
    };
    look();
    window.addEventListener("focus", look);
    return () => {
      live = false;
      window.removeEventListener("focus", look);
    };
  }, [extensionPresent]);

  const released = isReportReleased(report.stage);
  const preview = released ? report.assembledText : previewText(report, draft);
  const count = preview.length;
  const transitions = offeredTransitions(report);
  /**
   * Ruling D-4 (§G): a report holding no text may not be marked `AI Drafted` or `TD Drafted`,
   * and the API refuses those two transitions with `report_text_required`.
   *
   * **The measure is the SAVED report, not the box.** The route reads what is stored, so this
   * reads `assembledText` — the server's own serialisation of the stored report, and never
   * anything derived from the live textarea. Gating on the preview enabled a press the server
   * then refused with "this report holds no text" while the text sat on screen, which reads as
   * the system contradicting itself; and it withheld a press on saved text the server would
   * have accepted whenever the box had been cleared. Trimmed-empty is empty.
   */
  const holdsText = report.assembledText.trim() !== "";
  /**
   * Text in the box that the server has not been told about. It is not "no text" — the honest
   * line is not "add text" but "save it": the marker reads the saved report.
   */
  const awaitingSave = !holdsText && preview.trim() !== "";
  /**
   * What a press would send — the STORED report, built with the same builder the API uses, so
   * the dialogue, the clipboard, the Word download and the receipt all describe one report:
   * the one the route would push.
   */
  const pushPayload: TmsReportPayload = reportPushPayload(report, officialName);
  /**
   * The box has moved on from the stored report. Not a blocker — what the push would send is
   * real, stored text — but it must be said inside the dialogue, because a TD looking at
   * their own unsaved sentence has every reason to assume it is going too. Saying so is the
   * whole of the remedy: the save is not chained onto the push, and the push is not chained
   * onto the save. One press, one act.
   */
  const unsavedEdits = !released && preview !== report.assembledText;
  /**
   * The chip is raised by the SERVER's comparison of the accepted snapshot with the text as
   * it now stands, or by this session having saved text since the last push. The server half
   * is the honest one and survives a reload; the session half still covers the moment between
   * a save and the next fetch.
   */
  const showEditedChip = (report.editedSinceLastPush || editedSincePush) && !released;
  const markersWithheld = !holdsText && transitions.some(stageRequiresReportText);

  /** The server's report wins outright — stage, stored text and assembled text alike. */
  function reconcile(authoritative: ReportBody): void {
    setReport(authoritative);
    setDraft(
      (authoritative.ownedByUm ? authoritative.tdClosingRemarks : authoritative.tdDraftText) ?? "",
    );
  }

  async function commitText(): Promise<void> {
    const previous = report;
    const body = report.ownedByUm
      ? { tdClosingRemarks: draft === "" ? null : draft }
      : { tdDraftText: draft === "" ? null : draft };

    // NOT optimistic. The typed text is already on screen — it is in the box and in the
    // preview, both of which read `draft` — so writing a provisional report here would buy no
    // visible immediacy at all. What it *would* buy is a provisional `assembledText`, and that
    // field is what the D-4 markers below are gated on: an optimistic write would enable a
    // press on the strength of a save that has not landed. The server's reply is the only
    // thing that moves `report`.
    setBusy(true);
    setFailure(null);

    try {
      const saved = await saveText(previous.tmsPersonId, body);
      reconcile(saved);
      // Only a report that has actually been pushed can be out of step with TMS.
      if (saved.lastPushedAt !== null) setEditedSincePush(true);
    } catch (error) {
      // Nothing to put back: `report` never moved. The typed text stays in the box, unsent.
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The report was not saved. Your text is still here, unsent.",
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * The push (§B6), after the confirmation dialogue.
   *
   * The stage moves **because TMS accepted the text**, and the server is what says so: the
   * reply carries the stored report. A refusal leaves everything where it was, with the text
   * intact and a receipt saying what happened — never a stage that claims a push.
   */
  async function commitPush(): Promise<void> {
    setBusy(true);
    setFailure(null);

    setConfirming(false);
    try {
      const result = await push(report.tmsPersonId, setProgress);
      reconcile(result.report);
      setReceipt(result.receipt);
      setEditedSincePush(false);
      setExtensionReady(true);
    } catch (error) {
      setConfirming(false);
      if (error instanceof ReportPushNotLanded && error.recorded !== null) {
        // The press WAS recorded - a failed pair of receipts, or an accepted one whose stage did
        // not move: the stored receipt is shown, never a made-up one.
        reconcile(error.recorded.report);
        setReceipt(error.recorded.receipt);
        setFailure(error.message);
        return;
      }
      if (error instanceof ReportPushNotLanded && error.message === EXTENSION_ABSENT_TEXT) setExtensionReady(false);
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The report was not pushed. Nothing reached TMS; your text is still here.",
      );
      setReceipt({
        id: "unsent",
        path: report.ownedByUm ? "W3" : "W2",
        kind: "report",
        subjectTmsPersonId: report.tmsPersonId,
        tournamentId: report.tournamentId,
        pressedByTmsPersonId: "",
        // This receipt was never recorded — the request did not reach the API — so there is
        // no presser to name. Empty rather than the caller's own guess at who they are.
        pressedByMediaName: "",
        pressedAt: new Date().toISOString(),
        outcome: "failed",
        failureReason: error instanceof ApiError ? error.code.replace(/^tms_/, "") : null,
        detail:
          error instanceof ApiError
            ? error.message
            : "The report was not pushed. Nothing reached TMS.",
        payloadSummary: `${String(pushPayload.charCount)} characters of report text, not sent`,
        payload: pushPayload,
        tmsReference: null,
        transport: "unknown",
      });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  /**
   * A marker transition (§B9). The stage on screen is a claim about what the server holds, so
   * it is set from the reply and from nothing else: no optimistic `stage: to` before the
   * await. A slow write shows as a disabled control, which is where the waiting belongs; a
   * refused one leaves the pill exactly where it was.
   */
  async function commitStage(to: ReportStage): Promise<void> {
    const previous = report;
    setBusy(true);
    setFailure(null);

    try {
      const moved = await transition(previous.tmsPersonId, to);
      reconcile(moved);
      // A successful push is what makes TMS current again; nothing else clears the chip.
      if (to === 3) setEditedSincePush(false);
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "The report did not move. Nothing was sent to TMS.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel panel--d1" aria-labelledby="composerTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="composerTitle">
          {report.ownedByUm ? "TD Closing Remarks" : "TD Draft Report"}
        </h3>
        <span className="panel__note">
          {report.ownedByUm
            ? "3–4 Lines · Goes Into The Owning UM's Report"
            : "Your Version — This Is What Goes To TMS"}
        </span>
        <span className="spacer" />
        {report.lastPushedAt === null ? null : (
          /* RUN 83 — `pushTime`, which is the tournament's clock, and is what the `Marks Pushed`
             chip beside this one on the marking panel has always used. This chip had its own
             inline `toLocaleTimeString` with no `timeZone`, so the pair of them disagreed by the
             device's whole offset while claiming to be the same stamp. */
          <span className="pushchip" data-testid="last-pushed">
            <span aria-hidden="true">✓</span>Last Pushed {pushTime(report.lastPushedAt)}
          </span>
        )}
        <span
          className={`statuspill ${released ? "statuspill--good" : "statuspill--warn"}`}
          title={REPORT_STAGE_TOOLTIPS[report.stage]}
          data-testid="report-stage"
        >
          <span className="sdot" />
          {REPORT_STAGE_LABELS[report.stage]}
        </span>
        {showEditedChip ? (
          <span
            className="editchip"
            title="This text has been saved here since the last push. TMS still holds the earlier version until it is pushed again."
            data-testid="edited-since-push"
          >
            Edited Since Last Push
          </span>
        ) : null}
      </div>

      <div className="airep">
        {released ? (
          <div className="hidenote" data-testid="released-note">
            <b>Published in TMS —</b> this report is confirmed and can no longer be edited here.
            TMS holds the published record, and the Control Centre reads that stage back rather
            than setting it.
          </div>
        ) : null}

        <label className="composerlabel" htmlFor="composerText" data-howto="td-composer">
          {report.ownedByUm
            ? `Your closing remarks for ${officialName} — they head the owning UM's report`
            : `Your report on ${officialName}`}
        </label>
        <textarea
          id="composerText"
          className="aitext"
          data-testid="composer-text"
          aria-label={report.ownedByUm ? "TD Closing Remarks" : "TD Draft Report"}
          placeholder={report.ownedByUm ? REMARKS_PLACEHOLDER : DRAFT_PLACEHOLDER}
          value={draft}
          readOnly={released}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />

        <div className="aibar">
          {released ? null : (
            <button
              type="button"
              className="gbtn gbtn--solid"
              data-testid="save-text"
              disabled={busy}
              onClick={() => void commitText()}
            >
              {report.ownedByUm ? "Save Closing Remarks" : "Save Draft"}
            </button>
          )}
          <span className={counterClass(count)} title={counterTitle(count)} data-testid="charcount">
            {count} / {LIMIT}
          </span>
          <span className="btnhint" data-testid="charcount-caption">
            {COUNT_CAPTION}
          </span>
          <span className="btnhint">
            {report.ownedByUm
              ? "The preview includes the owning Umpires Manager's narrative as well as your remarks. The prefixes are added when the report is put together, never typed and never stored."
              : "The Technical Delegate authors this report in full — no prefix convention applies."}
          </span>
        </div>

        {failure === null ? null : (
          <div className="writeerror" role="alert" data-testid="write-error">
            <b>Not Saved</b>
            {failure}
          </div>
        )}

        <div className="composerprev" data-howto="report-preview">
          <span className="composerlabel">{PREVIEW_LABEL}</span>
          <pre className="previewbox" data-testid="composer-preview">
            {preview}
          </pre>
          {preview === "" ? (
            <div className="hidenote">
              <b>Blank —</b> nothing has been written yet. <b>Blank</b> is a real state on the
              Reporting page, not an absence.
            </div>
          ) : null}
        </div>

        <div className="composerbar" data-testid="stage-actions">
          {transitions.map((to) => (
            <button
              key={to}
              type="button"
              className={to === 3 ? "gbtn gbtn--solid" : "gbtn"}
              data-testid={`stage-action-${to}`}
              disabled={busy || confirming || (stageRequiresReportText(to) && !holdsText)}
              onClick={() => {
                // §B6's push is the one transition that goes to TMS, so it goes through the
                // confirmation dialogue (gate 4) and through the push route. The two marker
                // transitions record a draft and send nothing anywhere.
                if (to === 3) setConfirming(true);
                else void commitStage(to);
              }}
            >
              {reportStageActionLabel(report.stage, to)}
            </button>
          ))}

          {/* Ruling D-4 — the markers are withheld, not hidden: the row keeps its shape and
              says what would let the press through. A disabled control with no explanation
              reads as a bug. */}
          {markersWithheld ? (
            <span className="btnhint" data-testid="stage-text-required">
              {awaitingSave ? SAVE_FIRST_HINT : REPORT_TEXT_REQUIRED_HINT}
            </span>
          ) : null}

          {/* RUN 96: on an umpire the TD pushes the whole assembled report - his remarks above
              the owning UM's narrative - with the agreed marks; the narrative stays hers. */}
          {report.ownedByUm && !released ? (
            <span className="btnhint" data-testid="um-pushes">
              {report.ownerUmShortName === null
                ? "The owning Umpires Manager writes the narrative. Push sends the whole report - your remarks above hers - with the agreed marks."
                : `${report.ownerUmShortName}, the owning Umpires Manager, writes the narrative. Push sends the whole report - your remarks above hers - with the agreed marks.`}
            </span>
          ) : null}

          {transitions.length === 0 && !(report.ownedByUm && !released) ? (
            <span className="btnhint" data-testid="no-transitions">
              {released
                ? "No further step here — publishing is confirmed inside TMS."
                : "No stage transition is available from here."}
            </span>
          ) : null}

          {transitions.includes(3) ? (
            <span className="btnhint">
              Pushed as draft, with the marks, through the TMS Push extension. Publishing remains a
              deliberate step inside TMS.
            </span>
          ) : null}
        </div>

        {transitions.includes(3) && extensionReady === false && progress === null ? (
          <div className="hidenote reportpush-absent" data-testid="extension-absent">
            <b>TMS Push extension not answering —</b> {EXTENSION_ABSENT_TEXT}
          </div>
        ) : null}

        {progress === null ? null : (
          <div className="reportpush-progress" role="status" aria-live="polite" data-testid="push-progress">
            {progress}
          </div>
        )}

        {confirming ? (
          <PushConfirm
            payload={pushPayload}
            officialName={officialName}
            confirmLabel={report.stage === 3 ? "Confirm Repush" : "Confirm Push"}
            busy={busy}
            unsavedEdits={unsavedEdits}
            note={EXTENSION_PUSH_NOTE}
            {...(pushPayload.charCount > LIMIT
              ? {
                  blocker:
                    `${pushPayload.charCount} characters — TMS rejects anything over ${LIMIT}. ` +
                    `Trim ${pushPayload.charCount - LIMIT}.`,
                }
              : pushPayload.text.trim() === ""
                ? { blocker: "The report is empty." }
                : {})}
            onConfirm={() => void commitPush()}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        ) : null}

        {receipt === null ? null : <PushReceiptNote receipt={receipt} />}

        {/* §4: the fallbacks ship BESIDE the push, not after it fails — and they render from
            state the client already holds, so they work when TMS cannot be reached at all.
            They carry the SAME payload as the push: the stored report. A record handed out of
            the Control Centre must be the record the Control Centre holds, and where the box
            has moved on that is said here rather than left to be discovered in Word. */}
        {released ? null : (
          <>
            <PushFallbacks payload={pushPayload} />
            {unsavedEdits ? (
              <span className="btnhint" data-testid="fallbacks-unsaved-edits">
                Both carry the saved report. Your unsaved edits are not in them — save first to
                include them.
              </span>
            ) : null}
          </>
        )}
      </div>

      {report.ownedByUm ? (
        <div className="panel__foot">
          <b>Owned by the Umpires Manager —</b> the narrative below is the owning UM's, drafted
          and pushed to TMS from the UM app. The Technical Delegate's one authoring surface on
          an umpire row is the closing remarks above.
          <pre className="previewbox previewbox--muted" data-testid="um-body">
            {report.tdDraftText ?? "No narrative from the owning UM yet."}
          </pre>
        </div>
      ) : (
        <div className="panel__foot">
          <b>One report per official, for the whole tournament —</b> there is no per-competition
          report and no second row. Pushed text lands in TMS <b>as draft</b>;
          publishing stays a deliberate step you take inside TMS.
        </div>
      )}
    </section>
  );
}
